import styled from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { fadeOut, fadeUp, reducedMotion, shake } from '../../atome/motion/motion.styled';

export const Wrapper = styled.div`
  min-height: 100vh;
  padding: 3rem 1.25rem 5rem;
  overflow-x: clip;
  background:
    radial-gradient(70% 60% at 50% 0%, ${color.sandLight} 0%, transparent 60%),
    ${color.cream};
`;

export const Shell = styled.div`
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
`;

export const TopBar = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 2.4rem;

  h1 {
    font-family: ${font.display};
    font-size: clamp(2rem, 5vw, 2.8rem);
    font-weight: 600;
    line-height: 1.1;
    color: ${color.ink};
    margin: 0.7rem 0 0.3rem;
  }

  p {
    font-family: ${font.body};
    font-size: 0.86rem;
    color: ${color.inkSoft};
    margin: 0;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.4rem;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  color: ${color.forestDeep};
  background: transparent;
  border: 1px solid rgba(74, 112, 97, 0.28);
  transition:
    background ${duration.base} ${ease.out},
    border-color ${duration.base} ${ease.out};

  &:hover:not(:disabled) {
    background: #fff;
    border-color: ${color.forest};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 1.6rem;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: #fff;
  background: ${color.forest};
  box-shadow: 0 14px 30px rgba(74, 112, 97, 0.24);
  transition:
    transform ${duration.base} ${ease.spring},
    background ${duration.base} ${ease.out};

  &:hover:not(:disabled) {
    background: ${color.forestDeep};
    transform: translateY(-2px);
  }

  &:disabled {
    background: ${color.sandDeep};
    cursor: not-allowed;
    box-shadow: none;
  }

  ${reducedMotion};
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.6rem;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const CountPill = styled.span`
  font-family: ${font.body};
  font-size: 0.76rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${color.inkFaint};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(19rem, 1fr));
  gap: 1.2rem;
`;

/* ------------------------------------------------------------------ *
 * Tabs — Products / Orders
 * ------------------------------------------------------------------ */

export const TabRail = styled.nav`
  display: inline-flex;
  gap: 0.3rem;
  margin-bottom: 1.8rem;
  padding: 0.3rem;
  border-radius: ${radius.pill};
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid ${color.line};
`;

export const Tab = styled.button<{ $active: boolean }>`
  padding: 0.55rem 1.4rem;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.86rem;
  letter-spacing: 0.04em;
  color: ${({ $active }) => ($active ? '#fff' : color.inkSoft)};
  background: ${({ $active }) => ($active ? color.forest : 'transparent')};
  transition: background ${duration.base} ${ease.out}, color ${duration.base} ${ease.out};

  &:hover {
    color: ${({ $active }) => ($active ? '#fff' : color.forestDeep)};
  }
`;

/* ------------------------------------------------------------------ *
 * Orders
 * ------------------------------------------------------------------ */

export const OrderList = styled.div`
  display: grid;
  gap: 1rem;
`;

export const OrderCard = styled.article`
  padding: 1.2rem 1.3rem;
  border-radius: ${radius.lg};
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow: ${shadow.soft};
`;

export const OrderTop = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding-bottom: 0.9rem;
  margin-bottom: 0.9rem;
  border-bottom: 1px solid ${color.lineSoft};

  h3 {
    font-family: ${font.display};
    font-size: 1.35rem;
    font-weight: 600;
    color: ${color.ink};
    margin: 0;
  }

  time {
    display: block;
    font-family: ${font.body};
    font-size: 0.76rem;
    letter-spacing: 0.06em;
    color: ${color.inkFaint};
  }
`;

export const OrderTotal = styled.div`
  text-align: right;
  font-family: ${font.body};

  strong {
    display: block;
    font-family: ${font.display};
    font-size: 1.5rem;
    font-weight: 600;
    color: ${color.forestDeep};
  }

  del {
    font-size: 0.78rem;
    color: ${color.inkFaint};
  }
`;

export const OrderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: 1rem;
`;

export const OrderField = styled.div`
  font-family: ${font.body};
  min-width: 0;

  span {
    display: block;
    font-size: 0.7rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${color.inkFaint};
    margin-bottom: 0.3rem;
  }

  p {
    font-size: 0.86rem;
    line-height: 1.6;
    color: ${color.ink};
    margin: 0;
    overflow-wrap: anywhere;
  }

  a {
    color: ${color.forestDeep};
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

/* ---- order filters ---- */

export const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.9rem;
`;

export const FilterField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-family: ${font.body};
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${color.inkFaint};
`;

/**
 * A native select rather than the MUI one: the toolbar sits above a long list
 * and the platform picker is the fastest thing to reach on a phone.
 */
export const Select = styled.select`
  appearance: none;
  padding: 0.55rem 2.1rem 0.55rem 0.9rem;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.85rem;
  color: ${color.ink};
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid ${color.line};
  /* Chevron drawn inline so the control needs no extra wrapper element. */
  background-image: linear-gradient(45deg, transparent 50%, ${color.inkSoft} 50%),
    linear-gradient(135deg, ${color.inkSoft} 50%, transparent 50%);
  background-position: calc(100% - 1.05rem) 55%, calc(100% - 0.75rem) 55%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  transition:
    border-color ${duration.fast} ${ease.out},
    background-color ${duration.fast} ${ease.out};

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${color.forestSoft};
    background-color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** The per-order status picker, tinted like the Badge it replaced. */
export const StatusSelect = styled(Select)<{ $tone?: 'sand' | 'forest' | 'danger' | 'warning' }>`
  padding: 0.35rem 1.9rem 0.35rem 0.7rem;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  text-transform: capitalize;
  ${({ $tone }) => {
    switch ($tone) {
      case 'forest':
        return `background-color: rgba(74, 112, 97, 0.1); color: ${color.forestDeep};`;
      case 'danger':
        return `background-color: rgba(180, 84, 74, 0.11); color: ${color.danger};`;
      case 'warning':
        return `background-color: rgba(181, 132, 63, 0.13); color: ${color.warning};`;
      default:
        return `background-color: rgba(226, 213, 196, 0.45); color: ${color.inkSoft};`;
    }
  }}
`;

export const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  margin-top: 1.4rem;
  font-family: ${font.body};
  font-size: 0.8rem;
  color: ${color.inkSoft};
`;

/* ---- order line items ---- */

export const ItemsBlock = styled.div`
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid ${color.lineSoft};
`;

export const ItemsHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.6rem;
  font-family: ${font.body};
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${color.inkFaint};
`;

export const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: baseline;
  gap: 0.5rem 1rem;
  padding: 0.45rem 0;
  font-family: ${font.body};
  font-size: 0.86rem;
  color: ${color.ink};

  & + & {
    border-top: 1px dashed ${color.lineSoft};
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr auto;
  }
`;

export const ItemName = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;

  a {
    color: ${color.forestDeep};
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  /* The "product removed" note, when the line outlived its product. */
  small {
    display: block;
    font-size: 0.72rem;
    color: ${color.inkFaint};
  }
`;

export const ItemMeta = styled.span`
  font-size: 0.8rem;
  color: ${color.inkFaint};
  white-space: nowrap;

  @media (max-width: 520px) {
    grid-column: 1;
  }
`;

export const ItemTotal = styled.strong`
  font-weight: 600;
  color: ${color.forestDeep};
  white-space: nowrap;
  text-align: right;
`;

export const Card = styled.article`
  display: flex;
  flex-direction: column;
  padding: 1.1rem;
  border-radius: ${radius.lg};
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow: ${shadow.soft};
  transition: box-shadow ${duration.base} ${ease.out};

  &:hover {
    box-shadow: ${shadow.lift};
  }

  ${reducedMotion};
`;

export const CardTop = styled.div`
  display: flex;
  gap: 1rem;
`;

export const Thumb = styled.div`
  flex: 0 0 auto;
  width: 84px;
  height: 84px;
  border-radius: ${radius.md};
  overflow: hidden;
  background: ${color.creamDeep};
  display: grid;
  place-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  span {
    font-family: ${font.display};
    font-size: 1.6rem;
    color: ${color.sandDeep};
  }
`;

export const CardBody = styled.div`
  min-width: 0;
  flex: 1;

  h3 {
    font-family: ${font.display};
    font-size: 1.25rem;
    font-weight: 600;
    color: ${color.ink};
    margin: 0 0 0.15rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    display: block;
    font-family: ${font.body};
    font-size: 0.72rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${color.inkFaint};
    margin-bottom: 0.5rem;
  }
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-family: ${font.body};

  strong {
    font-size: 1.05rem;
    font-weight: 600;
    color: ${color.forestDeep};
  }

  del {
    font-size: 0.82rem;
    color: ${color.inkFaint};
  }
`;

export const Badge = styled.span<{ $tone?: 'sand' | 'forest' | 'danger' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  border-radius: ${radius.pill};
  font-family: ${font.body};
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  ${({ $tone }) => {
    switch ($tone) {
      case 'forest':
        return `background: rgba(74, 112, 97, 0.1); color: ${color.forestDeep};`;
      case 'danger':
        return `background: rgba(180, 84, 74, 0.11); color: ${color.danger};`;
      case 'warning':
        return `background: rgba(181, 132, 63, 0.13); color: ${color.warning};`;
      default:
        return `background: rgba(226, 213, 196, 0.45); color: ${color.inkSoft};`;
    }
  }}
`;

export const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.7rem;
`;

export const Bullets = styled.ul`
  /* The bottom margin keeps its distance from the pinned action row when the
     description is long enough to fill the card. */
  margin: 0.9rem 0 1.1rem;
  padding-left: 1.05rem;
  font-family: ${font.body};
  font-size: 0.82rem;
  line-height: 1.6;
  color: ${color.inkSoft};

  li {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
  /* Pinned to the bottom, so a two-line product and a six-line one still line
     their buttons up across the row. */
  margin-top: auto;
  padding-top: 0.9rem;
  border-top: 1px solid ${color.lineSoft};
`;

export const IconAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.8rem;
  background: transparent;
  color: ${({ $danger }) => ($danger ? color.danger : color.forestDeep)};
  border: 1px solid
    ${({ $danger }) => ($danger ? 'rgba(180, 84, 74, 0.26)' : 'rgba(74, 112, 97, 0.22)')};
  transition:
    background ${duration.fast} ${ease.out},
    border-color ${duration.fast} ${ease.out};

  &:hover:not(:disabled) {
    background: ${({ $danger }) => ($danger ? 'rgba(180, 84, 74, 0.07)' : 'rgba(74, 112, 97, 0.07)')};
    border-color: ${({ $danger }) => ($danger ? color.danger : color.forest)};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const Panel = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  border-radius: ${radius.lg};
  background: rgba(255, 255, 255, 0.8);
  border: 1px dashed ${color.line};

  h3 {
    font-family: ${font.display};
    font-size: 1.5rem;
    font-weight: 600;
    color: ${color.ink};
    margin: 0 0 0.4rem;
  }

  p {
    font-family: ${font.body};
    font-size: 0.9rem;
    color: ${color.inkSoft};
    margin: 0 0 1.4rem;
  }
`;

export const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.2rem;
  padding: 0.85rem 1.05rem;
  border-radius: ${radius.md};
  background: rgba(180, 84, 74, 0.09);
  border: 1px solid rgba(180, 84, 74, 0.28);
  color: ${color.danger};
  font-family: ${font.body};
  font-size: 0.86rem;
  line-height: 1.5;
  animation: ${shake} 0.45s ${ease.out};

  svg {
    flex: 0 0 auto;
  }

  ${reducedMotion};
`;

/**
 * Drifts in, holds, then fades out on its own. The fade is timed to land just
 * before the component clears the message (NOTICE_TIMEOUT_MS in admin.tsx), so
 * the banner is already invisible when it leaves the DOM.
 */
export const NoticeBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.2rem;
  padding: 0.85rem 1.05rem;
  border-radius: ${radius.md};
  background: rgba(74, 112, 97, 0.08);
  border: 1px solid rgba(74, 112, 97, 0.24);
  color: ${color.forestDeep};
  font-family: ${font.body};
  font-size: 0.86rem;
  animation: ${fadeUp} ${duration.base} ${ease.out},
    ${fadeOut} ${duration.slow} ${ease.out} 3.8s both;

  ${reducedMotion};
`;

/** Same field treatment as sign-in and checkout, so every form reads as one product. */
export const fieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: 'rgba(255,255,255,0.7)',
    transition: 'box-shadow 240ms ease, background-color 240ms ease',
    '& fieldset': { borderColor: 'rgba(74, 112, 97, 0.2)' },
    '&:hover fieldset': { borderColor: color.forestSoft },
    '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 8px 22px rgba(74,112,97,0.12)' },
    '&.Mui-focused fieldset': { borderColor: color.forest, borderWidth: '1px' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: color.forest },
};

export const dialogPaperSx = {
  borderRadius: radius.xl,
  background: color.cream,
  backgroundImage: `radial-gradient(70% 50% at 50% 0%, ${color.sandLight} 0%, transparent 70%)`,
  boxShadow: shadow.lift,
};

export const DialogHead = styled.div`
  padding: 1.8rem 1.8rem 0.4rem;

  h2 {
    font-family: ${font.display};
    font-size: 1.7rem;
    font-weight: 600;
    color: ${color.ink};
    margin: 0.6rem 0 0.25rem;
  }

  p {
    font-family: ${font.body};
    font-size: 0.85rem;
    color: ${color.inkSoft};
    margin: 0;
  }

  @media (max-width: 600px) {
    padding: 1.4rem 1.2rem 0.3rem;
  }
`;

export const DialogBody = styled.div`
  padding: 1.4rem 1.8rem 0.4rem;

  @media (max-width: 600px) {
    padding: 1.2rem 1.2rem 0.3rem;
  }
`;

export const DialogFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
  padding: 0.6rem 1.8rem 1.8rem;

  @media (max-width: 600px) {
    padding: 0.6rem 1.2rem 1.4rem;
  }
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 1rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

/* ------------------------------------------------------------------ *
 * Image uploader
 * ------------------------------------------------------------------ */

export const FieldLabel = styled.label`
  display: block;
  font-family: ${font.body};
  font-size: 0.78rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${color.inkFaint};
  margin-bottom: 0.6rem;
`;

export const HelperText = styled.p`
  font-family: ${font.body};
  font-size: 0.76rem;
  line-height: 1.5;
  color: ${color.inkFaint};
  margin: 0.55rem 0 1.4rem;
`;

export const DropZone = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 1.5rem 1rem;
  border-radius: ${radius.md};
  cursor: pointer;
  text-align: center;
  font-family: ${font.body};
  color: ${({ $active }) => ($active ? color.forestDeep : color.inkSoft)};
  background: ${({ $active }) => ($active ? 'rgba(74, 112, 97, 0.07)' : 'rgba(255, 255, 255, 0.7)')};
  border: 1px dashed ${({ $active }) => ($active ? color.forest : color.line)};
  transition:
    background ${duration.fast} ${ease.out},
    border-color ${duration.fast} ${ease.out};

  &:hover {
    border-color: ${color.forestSoft};
    background: #fff;
  }

  strong {
    font-size: 0.9rem;
    font-weight: 500;
    color: ${color.forestDeep};
  }

  span {
    font-size: 0.76rem;
  }
`;

export const ThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(6.2rem, 1fr));
  gap: 0.7rem;
  margin-bottom: 0.9rem;
`;

export const ThumbCard = styled.div<{ $cover: boolean }>`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${radius.md};
  overflow: hidden;
  background: ${color.creamDeep};
  border: 1px solid ${({ $cover }) => ($cover ? color.forest : color.lineSoft)};
  box-shadow: ${({ $cover }) => ($cover ? '0 8px 20px rgba(74, 112, 97, 0.18)' : 'none')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const ThumbTag = styled.span`
  position: absolute;
  left: 0.35rem;
  bottom: 0.35rem;
  padding: 0.15rem 0.5rem;
  border-radius: ${radius.pill};
  font-family: ${font.body};
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #fff;
  background: rgba(46, 80, 65, 0.85);
`;

export const ThumbButton = styled.button<{ $tone?: 'danger' }>`
  position: absolute;
  top: 0.3rem;
  display: grid;
  place-items: center;
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: ${({ $tone }) => ($tone === 'danger' ? color.danger : color.forestDeep)};
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 12px rgba(58, 48, 38, 0.18);
  transition: background ${duration.fast} ${ease.out};

  &:hover:not(:disabled) {
    background: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const RemoveButton = styled(ThumbButton)`
  right: 0.3rem;
`;

export const CoverButton = styled(ThumbButton)`
  left: 0.3rem;
`;

/** Placeholder tile shown while a file is on its way to the server. */
export const UploadingTile = styled.div`
  display: grid;
  place-items: center;
  aspect-ratio: 1;
  border-radius: ${radius.md};
  background: ${color.creamDeep};
  border: 1px dashed ${color.line};
`;

/** Live read-out of what the discount does to the shelf price. */
export const PricePreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0 0 1.4rem;
  padding: 0.85rem 1.1rem;
  border-radius: ${radius.md};
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid ${color.line};
  font-family: ${font.body};
  font-size: 0.84rem;
  color: ${color.inkSoft};

  strong {
    font-size: 1.05rem;
    color: ${color.forestDeep};
  }

  del {
    margin-left: 0.45rem;
    color: ${color.inkFaint};
  }
`;
